/* eslint-disable @typescript-eslint/no-unsafe-return */

import {i18nProvider} from "@trumpf/ux.i18n";
import {Fragment, FunctionalComponent, h, JSX} from "@stencil/core";

const {t} = i18nProvider;

export const Topic: FunctionalComponent<{
	headlineSlot: string;
	textSlot: string;
}> = ({headlineSlot, textSlot}) => (
	<div class="u-marginBottom-40">
		<slot name={headlineSlot} />
		<div class="text-normal">
			<slot name={textSlot} />
		</div>
	</div>
);

export const FormWrapper: FunctionalComponent<{
	id: string;
	action: string;
	onSubmit: (e: Event) => void;
}> = ({id, action, onSubmit}, children) => (
	<ux-form>
		<form id={id} action={action} method="POST" onSubmit={onSubmit}>
			{children}
		</form>
	</ux-form>
);

export const TableHeader: FunctionalComponent<{headerSlot: string}> = ({headerSlot}) => (
	<th>
		<slot name={headerSlot} />
	</th>
);

export const TableIconHeader: FunctionalComponent<{
	headerSlot: string;
	icon: "square-info" | "triangle-warning";
	tooltip: string;
}> = ({headerSlot, icon, tooltip}) => (
	<th>
		<slot name={headerSlot} />
		<ux-icon name={icon} weight="bold" style={{color: icon === "triangle-warning" ? "red" : "inherit"}} />
		<ux-tooltip tooltip-target={`ux-icon[name=${icon}]`} align="top">
			{tooltip}
		</ux-tooltip>
	</th>
);

export const TextCell: FunctionalComponent<{
	text: string;
	info?: {
		icon: "square-info" | "triangle-warning";
		tooltip: string;
	};
}> = ({text, info}) => (
	<td>
		<span>{text}</span>
		{info && (
			<Fragment>
				<ux-icon style={{color: info.icon === "triangle-warning" ? "red" : "inherit"}} name={info.icon} weight="bold" />
				<ux-tooltip tooltip-target={`ux-icon[name=${info.icon}]`} align="top">
					{info.tooltip}
				</ux-tooltip>
			</Fragment>
		)}
	</td>
);

export const CheckboxCell: FunctionalComponent<{
	name?: string;
	label?: string;
	disabled?: boolean;
	required?: boolean;
	isChecked: boolean;
	onChange: (e: Event) => void;
}> = ({name, label = "", disabled = false, required = false, isChecked, onChange}) => (
	<td>
		<ux-checkbox name={name} {...(label !== "" && {label})} disabled={disabled} required={required} checked={isChecked} onChange={onChange} />
	</td>
);

export const TableSkeleton: FunctionalComponent<{
	columns?: number;
	rows?: number;
}> = ({columns = 3, rows = 2}) => (
	<Fragment>
		{Array.from({length: rows}, (_, rowIndex) => (
			<tr key={rowIndex} class={"skeleton"}>
				{Array.from({length: columns}, (_, columnIndex) => (
					<td key={columnIndex} />
				))}
			</tr>
		))}
	</Fragment>
);

export const SettingsFooter: FunctionalComponent<{
	saveButtonSlot: string;
	cancelButtonSlot: string;
	hidden: boolean;
	unsavedChanges: boolean;
	statusIcon: JSX.Element;
}> = ({saveButtonSlot, cancelButtonSlot, hidden, unsavedChanges, statusIcon}) => (
	<div class={`uxn-notification-settings__footer ux-button-group-spacing ${!hidden && "visible"}`}>
		<button type="submit" disabled={!unsavedChanges} class={`ux-button ux-button--green ${!unsavedChanges && "ux-button--disabled"}`}>
			<slot name={saveButtonSlot} />
		</button>
		<button type="button" class={`ux-button ${!unsavedChanges && "hidden"}`} data-trigger-modal="open,cancel-settings-modal">
			<slot name={cancelButtonSlot} />
		</button>
		{statusIcon}
	</div>
);

export const CancelEditDialog: FunctionalComponent<{
	headlineSlot: string;
	cancelEditButtonSlot: string;
	continueEditButtonSlot: string;
	onCancelEdit: (e: Event) => void;
}> = ({headlineSlot, cancelEditButtonSlot, continueEditButtonSlot, onCancelEdit}) => {
	return (
		<ux-modal close-button-text={t("app.header.closeButton")} close-on-backdrop-click="true" opened="false" name="cancel-settings-modal">
			<ux-modal-content>
				<slot name={headlineSlot} />
			</ux-modal-content>

			<ux-modal-footer>
				<button type="button" data-trigger-modal="close,cancel-settings-modal" class="ux-button">
					<slot name={continueEditButtonSlot} />
				</button>
				<button data-trigger-modal="close,cancel-settings-modal" class="ux-button ux-button--green" onClick={onCancelEdit}>
					<slot name={cancelEditButtonSlot} />
				</button>
			</ux-modal-footer>
		</ux-modal>
	);
};
